import { Box, Group, Text, Stack } from '@mantine/core'
import { IconTimelineEvent } from '@tabler/icons-react'
import { EmptyState } from '@/components/ui'
import type { TrendPoint } from '@/api/security'

function fmtWhen(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} ${hh}:${mi}`
}

/** Abreviação por severidade: C=crítico, A=alto, M=médio, B=baixo, I=info. */
function sevCounts(p: TrendPoint): { label: string; v: number }[] {
  const s = p.findings_by_severity ?? {}
  return [
    { label: 'C', v: s.critical ?? 0 },
    { label: 'A', v: s.high ?? 0 },
    { label: 'M', v: s.medium ?? 0 },
    { label: 'B', v: s.low ?? 0 },
  ]
}

interface ScanTimelineProps {
  points: TrendPoint[]
}

/** Linha do tempo de scans, mais recente primeiro, com delta de total vs. o
 *  scan anterior. Com menos de 2 scans não há o que comparar → EmptyState. */
export function ScanTimeline({ points }: ScanTimelineProps) {
  if (points.length < 2) {
    return (
      <EmptyState
        icon={IconTimelineEvent}
        size="sm"
        title="Sem linha do tempo ainda"
        description="A comparação entre scans aparece a partir do segundo scan concluído."
      />
    )
  }

  // points vem antigo→recente; exibe recente→antigo.
  const ordered = [...points].reverse()

  return (
    <Stack gap="sm" data-testid="scan-timeline">
      {ordered.map((p, idx) => {
        const prev = ordered[idx + 1] // o mais antigo, para o delta
        const delta = prev ? p.total - prev.total : null
        let deltaLabel: string
        let deltaColor: string
        if (delta == null) {
          deltaLabel = 'baseline'
          deltaColor = 'var(--mantine-color-dimmed)'
        } else if (delta === 0) {
          deltaLabel = 'estável · 0 de diferença'
          deltaColor = 'var(--mantine-color-dimmed)'
        } else if (delta > 0) {
          deltaLabel = `▲ +${delta} vs. anterior`
          deltaColor = '#e03131'
        } else {
          deltaLabel = `▼ ${delta} vs. anterior`
          deltaColor = '#2f9e44'
        }
        return (
          <Box
            key={p.scan_id}
            style={{
              borderLeft: '3px solid #098d93',
              paddingLeft: 12,
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" ff="var(--mantine-font-family-monospace)">
                {fmtWhen(p.completed_at)} · #{p.scan_id}
              </Text>
              <Text size="xs" fw={600} style={{ color: deltaColor }}>{deltaLabel}</Text>
            </Group>
            <Group gap="md" mt={2}>
              {sevCounts(p).map((c) => (
                <Text key={c.label} size="xs" c="dimmed"
                      ff="var(--mantine-font-family-monospace)">
                  {c.v}{c.label}
                </Text>
              ))}
            </Group>
          </Box>
        )
      })}
    </Stack>
  )
}
